import { useRef } from "react";
import { IconChevronDown, IconDownload, IconFileImport, IconFileSpreadsheet } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { exportarExcel } from "@/shared/export/exportar-excel";
import { toastAdvertencia, toastErrorApi, toastExito } from "@/lib/toast";
import type { CFDIDeSolicitud, Prorrateo } from "@/lib/types";
import {
  prorrateoMultipleApi,
  type FilaDispersionMultipleInput,
} from "@/features/prorrateo-multiple/services/prorrateo-multiple.api";

const CONCEPTO_HEADERS = ["concepto", "conceptonumero", "numeroconcepto", "nconcepto"];
const CECO_HEADERS = ["centrodecosto", "centrodecostos", "ceco", "cecos", "centro", "centrocosto", "centrocostos"];
const CUENTA_HEADERS = ["cuenta", "cuentacontable", "cuentacont", "cta"];
const IMPORTE_HEADERS = ["importe", "monto", "subtotal", "total", "importemxn", "importesubtotal"];
const FACTURA_HEADERS = ["numerofactura", "numerodefactura", "factura", "folio", "nofactura", "numfactura"];

const norm = (s: string) => s.trim().toLowerCase().replace(/[\s._-]/g, "");

function aNumero(v: unknown): number {
  if (typeof v === "number") return v;
  const limpio = String(v ?? "").replace(/[^0-9.-]/g, "");
  return limpio === "" ? NaN : Number(limpio);
}

function extraer(fila: Record<string, unknown>, candidatos: string[]): unknown {
  for (const [k, v] of Object.entries(fila)) {
    if (candidatos.includes(norm(k))) return v;
  }
  return undefined;
}

/**
 * Menú de dispersión MULTI-factura para el prorrateo por slots (subtipo Múltiples
 * CFDI · paridad legado). Una sola plantilla con la columna "Número de factura"
 * (folio del CFDI) reparte los CeCos de TODOS los CFDIs a la vez; el backend empata
 * cada renglón con su slot por el folio. La plantilla se prellena con los folios y
 * las líneas actuales de cada factura.
 */
export function DispersionMultipleMenu({
  solicitudId,
  cfdis,
  prorrateos,
  onCambio,
}: {
  solicitudId: string;
  cfdis: CFDIDeSolicitud[];
  prorrateos: Prorrateo[];
  onCambio: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const importar = useMutation({
    mutationFn: (filas: FilaDispersionMultipleInput[]) =>
      prorrateoMultipleApi.importarDispersionMultiple(solicitudId, filas),
    onSuccess: (res) => {
      toastExito({
        title: "Dispersión cargada",
        message: `${res.slotsActualizados} factura(s) · ${res.lineasTotales} línea(s). Revisa y confirma cada CFDI.`,
      });
      onCambio();
    },
    onError: toastErrorApi,
  });

  function descargarPlantilla() {
    // Un renglón por (factura, línea actual). Si una factura no tiene líneas aún,
    // deja un renglón con su folio para que el usuario capture CeCo + importe.
    type Fila = { concepto: number; ceco: string; cuenta: string; importe: number; factura: string };
    const filas: Fila[] = [];
    for (const cfdi of cfdis) {
      const prorrateo = prorrateos.find((p) => p.numeroCfdi === cfdi.numeroSlot);
      const lineas = prorrateo?.lineas ?? [];
      if (lineas.length === 0) {
        filas.push({ concepto: 1, ceco: "", cuenta: "", importe: 0, factura: cfdi.folio });
      } else {
        for (const l of lineas)
          filas.push({
            concepto: l.conceptoNumero,
            ceco: l.centroDeCostoCodigo ?? "",
            cuenta: l.cuentaContableCodigo ?? "",
            importe: l.importe,
            factura: cfdi.folio,
          });
      }
    }
    exportarExcel<Fila>({
      archivo: "plantilla-dispersion-multifactura",
      hoja: "Dispersión",
      columnas: [
        { header: "Número de factura", value: (f) => f.factura },
        { header: "Concepto", value: (f) => f.concepto },
        { header: "Centro de costo", value: (f) => f.ceco },
        { header: "Cuenta contable", value: (f) => f.cuenta },
        { header: "Importe", value: (f) => f.importe },
      ],
      filas,
    });
  }

  async function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      if (!sheet) {
        toastAdvertencia({ title: "Archivo vacío", message: "El Excel no tiene hojas con datos." });
        return;
      }
      const crudas = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
      const parsed: FilaDispersionMultipleInput[] = [];
      for (const r of crudas) {
        const ceco = String(extraer(r, CECO_HEADERS) ?? "").trim();
        const cuenta = String(extraer(r, CUENTA_HEADERS) ?? "").trim();
        const importe = aNumero(extraer(r, IMPORTE_HEADERS));
        const numeroFactura = String(extraer(r, FACTURA_HEADERS) ?? "").trim();
        const conceptoRaw = aNumero(extraer(r, CONCEPTO_HEADERS));
        const conceptoNumero = Number.isFinite(conceptoRaw) && conceptoRaw > 0 ? Math.trunc(conceptoRaw) : 0;
        if (!ceco || !numeroFactura) continue;
        if (!Number.isFinite(importe) || importe <= 0) continue;
        parsed.push({
          conceptoNumero,
          centroDeCostoCodigo: ceco,
          cuentaContableCodigo: cuenta || undefined,
          importe: Math.round(importe * 100) / 100,
          numeroFactura,
        });
      }
      if (parsed.length === 0) {
        toastAdvertencia({
          title: "Sin renglones válidos",
          message: "Revisa las columnas 'Centro de costo', 'Importe' y 'Número de factura'.",
        });
        return;
      }
      importar.mutate(parsed);
    } catch {
      toastAdvertencia({ title: "No se pudo leer el archivo", message: "¿Es un .xlsx/.xls válido?" });
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            leftSection={<IconFileSpreadsheet size={14} />}
            rightSection={<IconChevronDown size={14} />}
            loading={importar.isPending}
          >
            Dispersión por Excel (todas las facturas)
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
            <IconFileImport size={16} stroke={1.7} />
            Cargar dispersión
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={descargarPlantilla}>
            <IconDownload size={16} stroke={1.7} />
            Descargar plantilla
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={(e) => void onArchivo(e)}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        tabIndex={-1}
        aria-hidden="true"
      />
    </>
  );
}
